import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { decodeId } from '@app/helpers/base64url';

interface RouteParamGuardProps {
    paramName: string;
    fallback: string;
    children: React.ReactNode;
}

/**
 * Validates an encoded ID route param before rendering the child component.
 * If the ID is missing, tampered, or invalid — shows a toast and redirects.
 * The child component never mounts, so no API calls happen.
 */
const RouteParamGuard = ({ paramName, fallback, children }: RouteParamGuardProps) => {
    const params = useParams();
    const navigate = useNavigate();
    const id = decodeId(params[paramName]);
    const isValid = id !== null && id > 0 && !isNaN(id);
    const [redirecting, setRedirecting] = useState(false);

    useEffect(() => {
        if (!isValid) {
            setRedirecting(true);
            toast.error('This link is invalid or has expired.');
            navigate(fallback, { replace: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!isValid || redirecting) {
        return null;
    }

    return <>{children}</>;
};

export default RouteParamGuard;
